<template>
  <div class="action-bar__initial-row">
    <div
      v-if="deliveryOptions && deliveryOptions.length > 0"
      class="action-bar__initial-item action-bar__initial-item--delivery"
    >
      <div class="action-bar__initial-label">
        {{
          $textDictionary['pdp.shippingTime.label'] ?? 'pdp.shippingTime.label'
        }}
        <info-icon
          v-if="deliveryOptionsTooltipText"
          :tooltip-text="deliveryOptionsTooltipText"
          :tooltip-max-width="350"
          tooltip-placement="top"
        />
      </div>
      <div v-if="isInitialLoading" class="action-bar__initial-field">
        <loading-skeleton style="width: 100%; height: 38px" />
      </div>
      <ul v-else class="action-bar__initial-shipping-list">
        <li
          v-for="option in deliveryOptions"
          :key="option.titleAddition"
          class="action-bar__initial-shipping-item"
        >
          <icon :name="option?.iconName ?? ''" size="small" />
          <span>{{ option.title }} {{ option.titleAddition }}</span>
        </li>
      </ul>
    </div>
    <div class="action-bar__initial-item action-bar__initial-item--price">
      <div class="action-bar__initial-label">
        {{ $textDictionary['pdp.netPrice.label'] ?? 'pdp.netPrice.label' }}
      </div>
      <loading-skeleton
        v-if="isInitialLoading"
        style="width: 200px; height: 38px"
      />

      <price-element
        v-else
        :current-price="priceText"
        :previous-price="previousPriceText"
        :discount="discountPercentageText"
        alignment="oneline"
        :subline="showTariffInformation ? pdpTariffText : ''"
      />
    </div>
    <div class="action-bar__initial-item action-bar__initial-item--amount">
      <div class="action-bar__initial-label">
        {{ $textDictionary['pdp.amount.label'] ?? 'pdp.amount.label' }}
      </div>
      <amount-input
        :model-value="amount"
        :min="1"
        :max="10"
        :disabled="isInitialLoading"
        @update:model-value="$emit('update:amount', $event)"
      />
    </div>
    <div class="action-bar__initial-item action-bar__initial-item--actions">
      <AlertBox
        v-if="maintenanceMode"
        class="mt-4 maintenance-mode"
        icon="attention"
        :style="'danger'"
      >
        <template #default>
          <LibRichTextRenderer :rich-text="maintenanceModeText" />
        </template>
      </AlertBox>
      <ClientOnly v-else>
        <Button
          v-if="displayAddToCartButton"
          style-type="attraction"
          icon-type="icon-left"
          icon-name="add-shopping-cart"
          :icon-scale="1.4"
          size="medium"
          :active-state="isInitialLoading ? 'initial' : addToCartButtonState"
          :disabled="isInitialLoading || addToCartButtonDisabled"
          @click="$emit('add-to-cart', 'cart', amount)"
        >
          {{ $textDictionary['pdp.addToCart.label'] ?? 'pdp.addToCart.label' }}
        </Button>
        <Button
          :style-type="
            !displayAddToCartButton ? 'attraction' : 'attraction-secondary'
          "
          size="medium"
          :active-state="isInitialLoading ? 'initial' : requestQuoteButtonState"
          :disabled="isInitialLoading || requestQuoteButtonDisabled"
          @click="$emit('add-to-cart', 'requestCart', amount)"
        >
          {{
            $textDictionary['pdp.requestButton.label'] ??
            'pdp.requestButton.label'
          }}
        </Button>
        <template #fallback>
          <div style="display: flex; gap: 8px; width: 100%">
            <loading-skeleton
              style="flex: 1; height: 40px; border-radius: 48px"
            />
            <loading-skeleton
              style="flex: 1; height: 40px; border-radius: 48px"
            />
          </div>
        </template>
      </ClientOnly>
    </div>
  </div>
  <div
    v-if="showSecondRow"
    class="action-bar__initial-row action-bar__initial-row--last"
  >
    <div class="action-bar__initial-item"></div>
  </div>
</template>

<script setup lang="ts">
import {
  Icon,
  LoadingSkeleton,
  InfoIcon,
  Button,
  PriceElement,
  AmountInput,
  AlertBox
} from '@hypercodestudio/basler-components';
import type { BtnStateType } from '@hypercodestudio/basler-components/dist/components/elements/Button.vue';
import type { DeliveryOption } from '@hypercodestudio/basler-components/dist/components/modules/ProductDetailAddToCart.vue';
import { type CartType } from '~/stores/cartStore/model';

const { $textDictionary, $globalPageSettings, $dictionary } = useNuxtApp();

const maintenanceModeText = $dictionary.value['maintenanceMode.text'];

const maintenanceMode = computed(() => {
  return $globalPageSettings.value?.shopConfig?.maintenanceMode;
});

interface Props {
  deliveryOptions: DeliveryOption[];
  deliveryOptionsTooltipText?: string;
  isInitialLoading: boolean;
  priceText: string;
  previousPriceText?: string;
  discountPercentageText?: string;
  showTariffInformation: boolean;
  pdpTariffText: string;
  amount: number;
  addToCartButtonState: BtnStateType;
  addToCartButtonDisabled: boolean;
  requestQuoteButtonState: BtnStateType;
  requestQuoteButtonDisabled: boolean;
  loggedIn: boolean;
  displayAddToCartButton: boolean;
  vscLink?: string;
  vscLinkText?: string;
}

const props = defineProps<Props>();

const showSecondRow = computed(() => {
  return false;
});

defineEmits<{
  'update:amount': [value: number];
  'add-to-cart': [type: CartType, amount: number];
}>();
</script>

<style scoped></style>
